// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { ascending } from "d3-array";
import * as React from "react";

export interface IconSVGProps extends React.SVGProps<SVGSVGElement> {
  color?: string;
}

// The ExternalPropsContext is used to dynamically pass along props from usages of the <Icon/> component to the IconSVGs
export const ExternalPropsContext = React.createContext<IconSVGProps>({});

export type IconSVGMap = {
  [name: string]: React.FC;
};
const IconSVG: IconSVGMap = {};

const BaseSVG = ({ children, viewBox, ...rest }: IconSVGProps) => (
  <svg viewBox={viewBox} xmlns="http://www.w3.org/2000/svg" {...rest}>
    {children}
  </svg>
);

const StrokeSVG = ({ children, ...propsFromInternalIcon }: IconSVGProps) => {
  const { color, ...propsFromExternalUser } =
    React.useContext(ExternalPropsContext);

  return (
    <BaseSVG
      {...propsFromInternalIcon}
      {...propsFromExternalUser}
      fill="transparent"
      stroke={color}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </BaseSVG>
  );
};

const FillSVG = ({ children, ...propsFromInternalIcon }: IconSVGProps) => {
  const { color, ...propsFromExternalUser } =
    React.useContext(ExternalPropsContext);

  return (
    <BaseSVG fill={color} {...propsFromInternalIcon} {...propsFromExternalUser}>
      {children}
    </BaseSVG>
  );
};

/*
  Process for adding an icon from Figma:
  1. Select group containing all paths for the icon from the layers list
  2. Right click -> Copy SVG
  3. Run SVG through SVGOMG (make sure the `Remove viewBox` option is disabled!) https://jakearchibald.github.io/svgomg/
  4. Remove `fill`/`stroke` properties from the icon, as these values are inherited from the `<svg/>` parent
  5. Add a functional component to `IconSVG`. Use `StrokeSVG` for icons that are built of stroked paths,
     `FillSVG` for icons that are built of filled paths
*/
IconSVG["Arrow"] = () => (
  <StrokeSVG viewBox="0 0 12 10">
    <path d="M11.5 5H0.5" />
    <path d="M7.5 1L11.5 5L7.5 9" />
  </StrokeSVG>
);

IconSVG["ArrowCircled"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M16 8a8 8 0 1 0-8 8 8.024 8.024 0 0 0 8-8Zm-7.707 3.955A.5.5 0 0 1 8 11.5V9H4a1 1 0 1 1 0-2h4V4.5a.5.5 0 0 1 .829-.376l4 3.5a.514.514 0 0 1 0 .752l-4 3.5a.5.5 0 0 1-.536.079Z" />
  </FillSVG>
);

IconSVG["Caret"] = () => (
  <FillSVG viewBox="0 0 12 10">
    <path d="M5.27641 9.59218C5.61662 10.1359 6.38338 10.1359 6.72359 9.59218L11.8593 1.38363C12.2322 0.78773 11.8202 0 11.1358 0H0.864242C0.179779 0 -0.232172 0.78773 0.140658 1.38363L5.27641 9.59218Z" />
  </FillSVG>
);

IconSVG["Check"] = () => (
  <FillSVG viewBox="0 0 8 6">
    <path d="M3.025 6.00002L0 3.00002L1 2.00002L3.025 4.00003L7 0L8 1L3.025 6.00002Z" />
  </FillSVG>
);

IconSVG["Clock"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM12 9H7V4H9V7H12V9Z" />
  </FillSVG>
);

IconSVG["Close"] = () => (
  <FillSVG viewBox="0 0 14 14">
    <path d="M13.7 0.3C13.3 -0.1 12.7 -0.1 12.3 0.3L7 5.6L1.7 0.3C1.3 -0.1 0.7 -0.1 0.3 0.3C-0.1 0.7 -0.1 1.3 0.3 1.7L5.6 7L0.3 12.3C-0.1 12.7 -0.1 13.3 0.3 13.7C0.5 13.9 0.7 14 1 14C1.3 14 1.5 13.9 1.7 13.7L7 8.4L12.3 13.7C12.5 13.9 12.8 14 13 14C13.2 14 13.5 13.9 13.7 13.7C14.1 13.3 14.1 12.7 13.7 12.3L8.4 7L13.7 1.7C14.1 1.3 14.1 0.7 13.7 0.3Z" />
  </FillSVG>
);

IconSVG["CloseOutlined"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM11.5 10.1L10.1 11.5L8 9.4L5.9 11.5L4.5 10.1L6.6 8L4.5 5.9L5.9 4.5L8 6.6L10.1 4.5L11.5 5.9L9.4 8L11.5 10.1Z" />
  </FillSVG>
);

IconSVG["DownChevron"] = () => (
  <FillSVG viewBox="0 0 8 4">
    <path d="M0 0L4 3.99988L8 0H0Z" />
  </FillSVG>
);

IconSVG["ChevronDown"] = () => (
  <StrokeSVG viewBox="0 0 24 24">
    <polyline points="6 9 12 15 18 9" />
  </StrokeSVG>
);

IconSVG["EllipsisVertical"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M8 4C9.10457 4 10 3.10457 10 2C10 0.89543 9.10457 0 8 0C6.89543 0 6 0.89543 6 2C6 3.10457 6.89543 4 8 4Z" />
    <path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" />
    <path d="M8 16C9.10457 16 10 15.1046 10 14C10 12.8954 9.10457 12 8 12C6.89543 12 6 12.8954 6 14C6 15.1046 6.89543 16 8 16Z" />
  </FillSVG>
);

IconSVG["Envelope"] = () => (
  <StrokeSVG viewBox="0 0 12 10">
    <path d="M11.5 5.5v4a1 1 0 01-1 1h-9a1 1 0 01-1-1v-4" />
    <path d="M11.5 3.5v-1a1 1 0 00-1-1h-9a1 1 0 00-1 1v1l5.5 3 5.5-3z" />
  </StrokeSVG>
);

IconSVG["Error"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M12 0C5.383 0 0 5.383 0 12C0 18.617 5.383 24 12 24C18.617 24 24 18.617 24 12C24 5.383 18.617 0 12 0ZM13.645 5L13 14H11L10.392 5H13.645ZM12 20C10.895 20 10 19.105 10 18C10 16.895 10.895 16 12 16C13.105 16 14 16.895 14 18C14 19.105 13.105 20 12 20Z" />
  </FillSVG>
);

IconSVG["Gift"] = () => (
  <StrokeSVG viewBox="0 0 12 12">
    <path d="M.5 3.5h11M2 2A1.424 1.424 0 013.333.5C5.305.5 6 3.5 6 3.5H3.333A1.423 1.423 0 012 2v0zM8.667 3.5H6s.7-3 2.667-3A1.424 1.424 0 0110 2a1.423 1.423 0 01-1.333 1.5v0zM10.5 5.5v5a1 1 0 01-1 1h-7a1 1 0 01-1-1v-5M6 5.5v6" />
  </StrokeSVG>
);

IconSVG["Hamburger"] = () => (
  <FillSVG viewBox="0 0 24 16">
    <path d="M23 9H1C0.448 9 0 8.553 0 8C0 7.447 0.448 7 1 7H23C23.552 7 24 7.447 24 8C24 8.553 23.552 9 23 9Z" />
    <path d="M23 2H1C0.448 2 0 1.553 0 1C0 0.447 0.448 0 1 0H23C23.552 0 24 0.447 24 1C24 1.553 23.552 2 23 2Z" />
    <path d="M23 16H12C11.448 16 11 15.553 11 15C11 14.447 11.448 14 12 14H23C23.552 14 24 14.447 24 15C24 15.553 23.552 16 23 16Z" />
  </FillSVG>
);

IconSVG["Info"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M12 0C5.383 0 0 5.383 0 12C0 18.617 5.383 24 12 24C18.617 24 24 18.617 24 12C24 5.383 18.617 0 12 0ZM14.658 18.284C13.997 18.544 11.706 19.638 10.386 18.475C9.992 18.129 9.796 17.69 9.796 17.157C9.796 16.159 10.124 15.289 10.715 13.2C10.819 12.805 10.946 12.293 10.946 11.887C10.946 11.186 10.68 11 9.959 11C9.607 11 9.217 11.125 8.864 11.257L9.059 10.458C9.846 10.138 10.834 9.748 11.68 9.748C12.949 9.748 13.883 10.381 13.883 11.585C13.883 11.932 13.823 12.54 13.697 12.96L12.967 15.542C12.816 16.064 12.543 17.215 12.966 17.556C13.382 17.893 14.367 17.714 14.853 17.485L14.658 18.284ZM13.452 8C12.624 8 11.952 7.328 11.952 6.5C11.952 5.672 12.624 5 13.452 5C14.28 5 14.952 5.672 14.952 6.5C14.952 7.328 14.28 8 13.452 8Z" />
  </FillSVG>
);

IconSVG["ItemDelete"] = () => (
  <FillSVG viewBox="0 0 24 21">
    <path d="M23 6H1a1 1 0 000 2h22a1 1 0 100-2zM23 0H1a1 1 0 000 2h22a1 1 0 100-2zM11 12H1a1 1 0 000 2h10a1 1 0 000-2zM11 18H1a1 1 0 000 2h10a1 1 0 000-2zM23.414 13L22 11.586l-3 3-3-3L14.586 13l3 3-3 3L16 20.414l3-3 3 3L23.414 19l-3-3 3-3z" />
  </FillSVG>
);

IconSVG["Leave"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="m23.625 9.219-5-4A1 1 0 0 0 17 6v3h-6a1 1 0 0 0 0 2h6v3a1 1 0 0 0 1.625.781l5-4a1 1 0 0 0 0-1.562Z" />
    <path d="M13 7a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1H1a1 1 0 0 0-1 1v17a1 1 0 0 0 .419.814l7 5A.988.988 0 0 0 8 24a1.019 1.019 0 0 0 .458-.11A1 1 0 0 0 9 23v-4h4a1 1 0 0 0 1-1v-4a1 1 0 0 0-2 0v3H9V6.016a1 1 0 0 0-.417-.816L4.112 2H12v4a1 1 0 0 0 1 1Z" />
  </FillSVG>
);

IconSVG["NeedsContact"] = () => (
  <FillSVG viewBox="0 0 24 23">
    <path d="M16.54 12.41a1 1 0 00-1.414 1.414l2.82 2.82-1.317 1.317-2.82-2.82a1 1 0 10-1.414 1.414l2.557 2.558c-.721.3-1.502.43-2.281.377a3.564 3.564 0 01-.8 1.923 7.09 7.09 0 001.135.1 6.982 6.982 0 004.95-2.047l2.108-2.136a1 1 0 00-.006-1.411l-3.518-3.51zM1.825 11.856c.298-.291.644-.528 1.023-.7A6.052 6.052 0 019.086 2.09l.433-.433a9.19 9.19 0 011.385-1.14A8.068 8.068 0 001.324 12.5c.143-.233.31-.449.5-.644zM10.943 17.28l-4.718-4.717a2.61 2.61 0 10-3.693 3.693l4.718 4.717a2.611 2.611 0 003.693-3.692z" />
    <path d="M21.64 2.364a8.071 8.071 0 00-11.414 0L5.589 7l.708.707a3.125 3.125 0 004.414 0l1.793-1.791 8.424 8.576.714-.714a8.071 8.071 0 000-11.414h-.002z" />
  </FillSVG>
);

IconSVG["NeedsEmployment"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M2 24h20a1 1 0 001-1v-3H1v3a1 1 0 001 1zM23 4h-6V1a1 1 0 00-1-1H8a1 1 0 00-1 1v3H1a1 1 0 00-1 1v12a1 1 0 001 1h22a1 1 0 001-1V5a1 1 0 00-1-1zM9 2h6v2H9V2zm7 13H8v-4h8v4z" />
  </FillSVG>
);

IconSVG["NeedsRiskAssessment"] = () => (
  <FillSVG viewBox="0 0 22 24">
    <path d="M21 0H1a1 1 0 00-1 1v22a1 1 0 001 1h20a1 1 0 001-1V1a1 1 0 00-1-1zm-2 21H3V3h3v3h3a2 2 0 114 0h3V3h3v18z" />
    <path d="M5 9h12v3H5V9zM5 14h12v3H5v-3z" />
  </FillSVG>
);

IconSVG["Open"] = () => (
  <StrokeSVG viewBox="0 0 11 11">
    <path d="M9.5 7.5V9.5C9.5 9.76522 9.39464 10.0196 9.20711 10.2071C9.01957 10.3946 8.76522 10.5 8.5 10.5H1.5C1.23478 10.5 0.98043 10.3946 0.792893 10.2071C0.605357 10.0196 0.5 9.76522 0.5 9.5V2.5C0.5 2.23478 0.605357 1.98043 0.792893 1.79289C0.98043 1.60536 1.23478 1.5 1.5 1.5H3.5" />
    <path d="M6.5 0.5H10.5V4.5" />
    <path d="M10.5 0.5L4.5 6.5" />
  </StrokeSVG>
);

IconSVG["Place"] = () => (
  <FillSVG viewBox="0 0 12 12">
    <path d="M6 0C3.075 0 0.75 2.325 0.75 5.25C0.75 6.675 1.275 8.025 2.325 9C2.4 9.075 5.4 11.775 5.475 11.85C5.775 12.075 6.225 12.075 6.45 11.85C6.525 11.775 9.6 9.075 9.6 9C10.65 8.025 11.175 6.675 11.175 5.25C11.25 2.325 8.925 0 6 0ZM6 6.75C5.175 6.75 4.5 6.075 4.5 5.25C4.5 4.425 5.175 3.75 6 3.75C6.825 3.75 7.5 4.425 7.5 5.25C7.5 6.075 6.825 6.75 6 6.75Z" />
  </FillSVG>
);

IconSVG["Search"] = () => (
  <StrokeSVG viewBox="0 0 15 15">
    <path
      d="M1.00016 14L5.22949 9.77065"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="M9.00004 11.3333C6.05452 11.3333 3.66671 8.94552 3.66671 6C3.66671 3.05448 6.05452 0.666668 9.00004 0.666668C11.9456 0.666668 14.3334 3.05448 14.3334 6C14.3334 8.94552 11.9456 11.3333 9.00004 11.3333Z"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
  </StrokeSVG>
);

IconSVG["Star"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M15.1441 5.43918L10.8271 4.81118L8.90008 0.900179C8.80851 0.742644 8.67719 0.611904 8.51925 0.521028C8.36132 0.430153 8.18229 0.382324 8.00008 0.382324C7.81786 0.382324 7.63884 0.430153 7.4809 0.521028C7.32297 0.611904 7.19165 0.742644 7.10008 0.900179L5.17308 4.81218L0.856079 5.43918C0.671214 5.46569 0.497468 5.54344 0.354521 5.66363C0.211573 5.78381 0.105137 5.94162 0.0472671 6.11918C-0.0106025 6.29675 -0.0175924 6.48697 0.0270889 6.6683C0.0717703 6.84963 0.166337 7.01483 0.300079 7.14518L3.42308 10.1902L2.68608 14.4902C2.65457 14.6741 2.67514 14.8631 2.74546 15.036C2.81578 15.2088 2.93305 15.3585 3.08401 15.4681C3.23497 15.5778 3.41359 15.643 3.59969 15.6565C3.78579 15.6699 3.97193 15.631 4.13708 15.5442L8.00008 13.5132L11.8611 15.5422C12.0262 15.629 12.2124 15.6679 12.3985 15.6545C12.5846 15.641 12.7632 15.5758 12.9142 15.4661C13.0651 15.3565 13.1824 15.2068 13.2527 15.034C13.323 14.8611 13.3436 14.6721 13.3121 14.4882L12.5751 10.1882L15.7001 7.14518C15.8335 7.01492 15.9278 6.84996 15.9724 6.66892C16.017 6.48788 16.0102 6.29797 15.9525 6.12065C15.8949 5.94332 15.7889 5.78563 15.6464 5.66539C15.5039 5.54515 15.3306 5.46714 15.1461 5.44018L15.1441 5.43918Z" />
  </FillSVG>
);

// Same path as Star, viewbox is slightly larger to accommodate stroke
IconSVG["StarOutline"] = () => (
  <StrokeSVG viewBox="-1 -1 18 18">
    <path d="M15.1441 5.43918L10.8271 4.81118L8.90008 0.900179C8.80851 0.742644 8.67719 0.611904 8.51925 0.521028C8.36132 0.430153 8.18229 0.382324 8.00008 0.382324C7.81786 0.382324 7.63884 0.430153 7.4809 0.521028C7.32297 0.611904 7.19165 0.742644 7.10008 0.900179L5.17308 4.81218L0.856079 5.43918C0.671214 5.46569 0.497468 5.54344 0.354521 5.66363C0.211573 5.78381 0.105137 5.94162 0.0472671 6.11918C-0.0106025 6.29675 -0.0175924 6.48697 0.0270889 6.6683C0.0717703 6.84963 0.166337 7.01483 0.300079 7.14518L3.42308 10.1902L2.68608 14.4902C2.65457 14.6741 2.67514 14.8631 2.74546 15.036C2.81578 15.2088 2.93305 15.3585 3.08401 15.4681C3.23497 15.5778 3.41359 15.643 3.59969 15.6565C3.78579 15.6699 3.97193 15.631 4.13708 15.5442L8.00008 13.5132L11.8611 15.5422C12.0262 15.629 12.2124 15.6679 12.3985 15.6545C12.5846 15.641 12.7632 15.5758 12.9142 15.4661C13.0651 15.3565 13.1824 15.2068 13.2527 15.034C13.323 14.8611 13.3436 14.6721 13.3121 14.4882L12.5751 10.1882L15.7001 7.14518C15.8335 7.01492 15.9278 6.84996 15.9724 6.66892C16.017 6.48788 16.0102 6.29797 15.9525 6.12065C15.8949 5.94332 15.7889 5.78563 15.6464 5.66539C15.5039 5.54515 15.3306 5.46714 15.1461 5.44018L15.1441 5.43918Z" />
  </StrokeSVG>
);

IconSVG["StarCircled"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M8 0C6.41775 0 4.87103 0.469192 3.55544 1.34824C2.23985 2.22729 1.21447 3.47672 0.608967 4.93853C0.00346625 6.40034 -0.15496 8.00887 0.153721 9.56072C0.462403 11.1126 1.22433 12.538 2.34315 13.6569C3.46197 14.7757 4.88743 15.5376 6.43928 15.8463C7.99113 16.155 9.59966 15.9965 11.0615 15.391C12.5233 14.7855 13.7727 13.7602 14.6518 12.4446C15.5308 11.129 16 9.58225 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0V0ZM10.629 11.618L8 10.236L5.371 11.618L5.871 8.691L3.747 6.618L6.686 6.191L8 3.528L9.314 6.191L12.253 6.618L10.127 8.691L10.629 11.618Z" />
  </FillSVG>
);

IconSVG["Success"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21509 0.913451 7.4078C0.00519943 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.8071 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0866C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6935 24 14.3734 24 12C23.9908 8.82024 22.7235 5.77336 20.4751 3.52492C18.2266 1.27648 15.1798 0.00923395 12 0V0ZM10 17.414L4.58601 12L6.00001 10.586L10 14.586L18 6.586L19.414 8L10 17.414Z" />
  </FillSVG>
);

IconSVG["TripleDot"] = () => (
  <FillSVG viewBox="0 0 16 4">
    <path d="M8 4C9.10457 4 10 3.10457 10 2C10 0.89543 9.10457 0 8 0C6.89543 0 6 0.89543 6 2C6 3.10457 6.89543 4 8 4Z" />
    <path d="M2 4C3.10457 4 4 3.10457 4 2C4 0.89543 3.10457 0 2 0C0.89543 0 0 0.89543 0 2C0 3.10457 0.89543 4 2 4Z" />
    <path d="M14 4C15.1046 4 16 3.10457 16 2C16 0.89543 15.1046 0 14 0C12.8954 0 12 0.89543 12 2C12 3.10457 12.8954 4 14 4Z" />
  </FillSVG>
);

IconSVG["UserDelete"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M8.9751 12C6.2141 12 3.9751 8.761 3.9751 6V5C3.9751 2.239 6.2141 0 8.9751 0C11.7361 0 13.9751 2.239 13.9751 5V6C13.9751 8.761 11.7361 12 8.9751 12Z" />
    <path d="M11.9741 19C11.9741 17.302 12.5791 15.746 13.5851 14.534C12.2391 14.231 10.6661 14 8.97412 14C6.15112 14 3.64612 14.638 2.00012 15.193C0.784121 15.603 -0.0258789 16.748 -0.0258789 18.031V22H12.6571C12.2241 21.09 11.9741 20.075 11.9741 19Z" />
    <path d="M23.4139 15.9999L21.9999 14.5859L18.9999 17.5859L15.9999 14.5859L14.5859 15.9999L17.5859 18.9999L14.5859 21.9999L15.9999 23.4139L18.9999 20.4139L21.9999 23.4139L23.4139 21.9999L20.4139 18.9999L23.4139 15.9999Z" />
  </FillSVG>
);

IconSVG["Users"] = () => (
  <FillSVG viewBox="0 0 18 18">
    <circle cx="5.75" cy="6.25" r="2.75" />
    <circle cx="12" cy="3.75" r="2.75" />
    <path d="M17.196 11.098A5.582 5.582 0 0 0 12 7.5a5.561 5.561 0 0 0-3.759 1.48c1.854.709 3.385 2.169 4.109 4.089.112.296.162.603.182.91 1.211-.05 2.409-.26 3.565-.646a1.786 1.786 0 0 0 1.041-.919c.2-.42.221-.888.059-1.316Z" />
    <path d="M10.946 13.598C10.135 11.446 8.047 10 5.75 10S1.365 11.446.554 13.598c-.162.429-.141.896.059 1.316.206.432.585.767 1.041.919a12.926 12.926 0 0 0 8.192 0 1.786 1.786 0 0 0 1.041-.919c.2-.42.221-.888.059-1.316Z" />
  </FillSVG>
);

IconSVG["Edit"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M14.1 0.3C13.7 -0.1 13.1 -0.1 12.7 0.3L6 7V10H9L15.7 3.3C16.1 2.9 16.1 2.3 15.7 1.9L14.1 0.3Z" />
    <path d="M15 9C14.4 9 14 9.4 14 10V14H2V2H6C6.6 2 7 1.6 7 1C7 0.4 6.6 0 6 0H1C0.4 0 0 0.4 0 1V15C0 15.6 0.4 16 1 16H15C15.6 16 16 15.6 16 15V10C16 9.4 15.6 9 15 9Z" />
  </FillSVG>
);

IconSVG["Minus"] = () => (
  <FillSVG viewBox="0 0 8 16">
    <path d="M8 7H0V9H8V7Z" />
  </FillSVG>
);

IconSVG["Journey"] = () => (
  <StrokeSVG viewBox="0 0 12 12">
    <path d="M2 11.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM10 3.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM2 6.5v-3a2 2 0 012-2v0a2 2 0 012 2v5a2 2 0 104 0v-3" />
  </StrokeSVG>
);

IconSVG["House"] = () => (
  <StrokeSVG viewBox="0 0 12 12">
    <path d="M.5 4.5L6 .5l5.5 4M10.5 6.5v4a1 1 0 01-1 1h-7a1 1 0 01-1-1v-4" />
    <path d="M6.5 4h-1a.5.5 0 00-.5.5v1a.5.5 0 00.5.5h1a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5z" />
    <path d="M4.5 11.5v-4h3v4" />
  </StrokeSVG>
);

IconSVG["Briefcase"] = () => (
  <StrokeSVG viewBox="0 0 12 12">
    <path d="M10.5 4.5h-9a1 1 0 00-1 1v5a1 1 0 001 1h9a1 1 0 001-1v-5a1 1 0 00-1-1zM3.5 2.5v-2h5v2M.5 8.5h11" />
  </StrokeSVG>
);

IconSVG["Calendar"] = () => (
  <StrokeSVG viewBox="0 0 30 30">
    <path d="M0.5 5H15.5" />
    <path d="M14 14.5H2C1.60218 14.5 1.22064 14.342 0.93934 14.0607C0.658035 13.7794 0.5 13.3978 0.5 13V3C0.5 2.60218 0.658035 2.22064 0.93934 1.93934C1.22064 1.65804 1.60218 1.5 2 1.5H14C14.3978 1.5 14.7794 1.65804 15.0607 1.93934C15.342 2.22064 15.5 2.60218 15.5 3V13C15.5 13.3978 15.342 13.7794 15.0607 14.0607C14.7794 14.342 14.3978 14.5 14 14.5V14.5Z" />
    <path d="M4.5 0.5L4.5 2.5" />
    <path d="M11.5 0.5L11.5 2.5" />
  </StrokeSVG>
);

IconSVG["CalendarSimple"] = () => (
  // The path lives in user-coord region (0,0)–(12,12). Offsetting the viewBox
  // origin by -2 in both axes centres the glyph inside a 16×16 box without
  // touching the path or changing the rendered size.
  <FillSVG width="16" height="16" viewBox="-2 -2 16 16">
    <path d="M11.25 2.25H9.75V0.75C9.75 0.336 9.41475 0 9 0C8.58525 0 8.25 0.336 8.25 0.75V2.25H3.75V0.75C3.75 0.336 3.41475 0 3 0C2.58525 0 2.25 0.336 2.25 0.75V2.25H0.75C0.33525 2.25 0 2.586 0 3V11.25C0 11.664 0.33525 12 0.75 12H11.25C11.6648 12 12 11.664 12 11.25V3C12 2.586 11.6648 2.25 11.25 2.25ZM10.5 10.5H1.5V4.5H10.5V10.5Z" />
  </FillSVG>
);

IconSVG["Return"] = () => (
  <StrokeSVG viewBox="0 0 19 9">
    <path
      d="M0.5 4.5H18.5V0.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 8.5L0.5 4.5L4.5 0.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </StrokeSVG>
);

IconSVG["Circle"] = () => (
  <FillSVG viewBox="0 0 8 8">
    <circle cx="4" cy="4" r="4" />
  </FillSVG>
);

IconSVG["AddFilled"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M12 0C9.62663 0 7.30655 0.703788 5.33316 2.02236C3.35977 3.34094 1.8217 5.21509 0.913451 7.4078C0.00519943 9.60051 -0.232441 12.0133 0.230582 14.3411C0.693605 16.6689 1.83649 18.8071 3.51472 20.4853C5.19295 22.1635 7.33115 23.3064 9.65892 23.7694C11.9867 24.2324 14.3995 23.9948 16.5922 23.0866C18.7849 22.1783 20.6591 20.6402 21.9776 18.6668C23.2962 16.6935 24 14.3734 24 12C23.9908 8.82024 22.7235 5.77336 20.4751 3.52492C18.2266 1.27648 15.1798 0.00923395 12 0V0ZM18 13H13V18H11V13H6.00001V11H11V6H13V11H18V13Z" />
  </FillSVG>
);

IconSVG["Download"] = () => (
  <StrokeSVG viewBox="0 0 16 16">
    <path
      d="M12.445 6.545c.022-.181.055-.358.055-.545A4.5 4.5 0 008 1.5c-2.359 0-4.271 1.821-4.463 4.13A3.993 3.993 0 00.5 9.5a4 4 0 004 4H12a3.5 3.5 0 003.5-3.5 3.489 3.489 0 00-3.055-3.455z"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 9l2.5 2.5L11 9M8.5 11.5v-6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </StrokeSVG>
);

IconSVG["Alert"] = () => (
  <FillSVG viewBox="0 0 16 15">
    <path d="M15.8 12.526L9.483.88A1.668 1.668 0 008.8.2a1.693 1.693 0 00-2.284.68L.2 12.526A1.678 1.678 0 001.686 15h12.628a1.7 1.7 0 001.308-.615 1.675 1.675 0 00.179-1.859H15.8zM8 13a1 1 0 110-2 1 1 0 010 2zm1-3.5a.5.5 0 01-.5.5h-1a.5.5 0 01-.5-.5v-4a.5.5 0 01.5-.5h1a.5.5 0 01.5.5v4z" />
  </FillSVG>
);

IconSVG["Pathways"] = () => (
  <FillSVG viewBox="0 0 32 32">
    <path d="M4 31C6.20914 31 8 29.2091 8 27C8 24.7909 6.20914 23 4 23C1.79086 23 0 24.7909 0 27C0 29.2091 1.79086 31 4 31Z" />
    <path d="M26.5 12.8596V24C26.5 26.4853 24.4853 28.5 22 28.5C19.5147 28.5 17.5 26.4853 17.5 24V8C17.5 3.85786 14.1421 0.5 10 0.5C5.85786 0.5 2.5 3.85786 2.5 8V19.1404C2.98588 19.0482 3.48731 19 4 19C4.51269 19 5.01412 19.0482 5.5 19.1404V8C5.5 5.51472 7.51472 3.5 10 3.5C12.4853 3.5 14.5 5.51472 14.5 8V24C14.5 28.1421 17.8579 31.5 22 31.5C26.1421 31.5 29.5 28.1421 29.5 24V12.8596C29.0141 12.9518 28.5127 13 28 13C27.4873 13 26.9859 12.9518 26.5 12.8596Z" />
    <path d="M28 9C30.2091 9 32 7.20914 32 5C32 2.79086 30.2091 1 28 1C25.7909 1 24 2.79086 24 5C24 7.20914 25.7909 9 28 9Z" />
  </FillSVG>
);

IconSVG["Workflows"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M1.07307 10.823C2.27318 12.0231 4.21895 12.0231 5.41906 10.823C6.61917 9.6229 6.61917 7.67713 5.41906 6.47701C4.21895 5.2769 2.27318 5.2769 1.07307 6.47701C-0.127048 7.67713 -0.127048 9.6229 1.07307 10.823Z" />
    <path d="M11.9535 22.8924C13.1536 24.0925 15.0994 24.0925 16.2995 22.8924C17.4996 21.6923 17.4996 19.7465 16.2995 18.5464C15.0994 17.3463 13.1536 17.3463 11.9535 18.5464C10.7534 19.7465 10.7534 21.6923 11.9535 22.8924Z" />
    <path d="M12.5017 0.700195C14.1091 0.700195 15.634 1.05359 17.0029 1.68693C16.9642 1.72339 16.9258 1.76057 16.8879 1.79847C16.4135 2.27286 16.0517 2.81716 15.8023 3.3982C14.7847 2.97971 13.6702 2.74892 12.5017 2.74892C10.2991 2.74892 8.28803 3.56902 6.7572 4.92053C6.21969 4.41383 5.59941 4.04536 4.94153 3.81514C6.88135 1.88972 9.55261 0.700195 12.5017 0.700195ZM17.6073 4.40775C18.9998 5.42158 20.0792 6.83936 20.6734 8.48887C21.3553 8.45269 22.0273 8.19048 22.5678 7.70226C21.8381 5.73369 20.5474 4.03747 18.8899 2.80772C18.6932 2.92989 18.5074 3.07636 18.3366 3.24713C17.9979 3.5858 17.7548 3.98384 17.6073 4.40775ZM21.185 11.4322C21.185 11.1185 21.1684 10.8088 21.1359 10.5037C21.8248 10.4193 22.4998 10.1951 23.1151 9.83113C23.1932 10.3535 23.2337 10.8881 23.2337 11.4322C23.2337 14.8381 21.6472 17.8734 19.1728 19.8394C19.0508 19.1352 18.7809 18.4517 18.3631 17.8387C20.0974 16.2512 21.185 13.9687 21.185 11.4322ZM11.1363 20.0087C11.5811 20.079 12.0372 20.1155 12.5017 20.1155C14.0239 20.1155 15.4546 19.7238 16.6986 19.0357C17.1043 19.6542 17.2637 20.3869 17.1768 21.0951C15.7636 21.7801 14.1775 22.1642 12.5017 22.1642C12.1242 22.1642 11.7513 22.1447 11.3839 22.1067C11.0535 21.454 10.971 20.708 11.1363 20.0087ZM9.16354 19.4506C6.7055 18.4261 4.81929 16.3043 4.11676 13.697C3.41231 13.818 2.68743 13.791 1.99208 13.6162C2.76801 17.3695 5.50394 20.4059 9.08219 21.6079C8.95681 20.8929 8.98392 20.1563 9.16354 19.4506ZM5.39034 6.44816C4.85928 5.93054 4.18728 5.6434 3.49985 5.58675C2.42014 7.24607 1.7868 9.22249 1.77005 11.3458C2.40204 11.6921 3.12999 11.7996 3.82158 11.6684C3.81949 11.5899 3.81843 11.5112 3.81843 11.4322C3.81843 9.57761 4.39985 7.85882 5.39034 6.44816Z" />
    <path d="M18.3364 7.59321C19.5365 8.79333 21.4823 8.79333 22.6824 7.59321C23.8825 6.3931 23.8825 4.44733 22.6824 3.24722C21.4823 2.0471 19.5365 2.0471 18.3364 3.24722C17.1363 4.44733 17.1363 6.3931 18.3364 7.59321Z" />
  </FillSVG>
);

IconSVG["Operations"] = () => (
  <FillSVG viewBox="0 0 32 32">
    <path d="M 20.0413 0.5148 C 18.7504 0.1788 17.3961 0 16 0 C 7.1634 0 0 7.1634 0 16 C 0 24.8366 7.1634 32 16 32 C 24.8366 32 32 24.8366 32 16 C 32 14.4317 31.7744 12.9161 31.3538 11.4839 C 30.5367 12.0755 29.6034 12.5166 28.5946 12.7662 C 28.8593 13.8001 29 14.8836 29 16 C 29 23.1797 23.1797 29 16 29 C 8.8203 29 3 23.1797 3 16 C 3 8.8203 8.8203 3 16 3 C 16.9773 3 17.9294 3.1078 18.845 3.3123 C 19.0636 2.2944 19.4759 1.3484 20.0413 0.5148" />
    <path d="M26.6667 9C28.8759 9 30.6667 7.20914 30.6667 5C30.6667 2.79086 28.8759 1 26.6667 1C24.4576 1 22.6667 2.79086 22.6667 5C22.6667 7.20914 24.4576 9 26.6667 9Z" />
    <path d="M16 21C18.7614 21 21 18.7614 21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21ZM16 24C20.4183 24 24 20.4183 24 16C24 11.5817 20.4183 8 16 8C11.5817 8 8 11.5817 8 16C8 20.4183 11.5817 24 16 24Z" />
  </FillSVG>
);

IconSVG["Insights"] = () => (
  <FillSVG viewBox="0 0 24 24">
    <path d="M21 8C19.55 8 18.74 9.44 19.07 10.51L15.52 14.07C15.22 13.98 14.78 13.98 14.48 14.07L11.93 11.52C12.27 10.45 11.46 9 10 9C8.55 9 7.73 10.44 8.07 11.52L3.51 16.07C2.44 15.74 1 16.55 1 18C1 19.1 1.9 20 3 20C4.45 20 5.26 18.56 4.93 17.49L9.48 12.93C9.78 13.02 10.22 13.02 10.52 12.93L13.07 15.48C12.73 16.55 13.54 18 15 18C16.45 18 17.27 16.56 16.93 15.48L20.49 11.93C21.56 12.26 23 11.45 23 10C23 8.9 22.1 8 21 8Z" />
    <path d="M15 9L15.94 6.93L18 6L15.94 5.07L15 3L14.08 5.07L12 6L14.08 6.93L15 9Z" />
    <path d="M3.5 11L4 9L6 8.5L4 8L3.5 6L3 8L1 8.5L3 9L3.5 11Z" />
  </FillSVG>
);

IconSVG["PencilRuler"] = () => (
  <StrokeSVG viewBox="0 0 32 32">
    <path d="m23.5 28.5 6-6m-17-17-6 6" strokeMiterlimit="10" />
    <path
      d="M10 15 3.5 8.5l6-6L16 9m7 7 6.5 6.5 1 7-7-1L17 22"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="m22.5 2.5-20 20 7 7 19.999-20-7-7Zm-10 10 3 3m-6 0 2 2m7-11 3 3m-6 0 2 2m-11 7 3 3"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
  </StrokeSVG>
);

IconSVG["Todo"] = () => (
  <StrokeSVG viewBox="0 0 32 32">
    <path
      d="m12.5 19.5 3 3 6-6m-2-12a3 3 0 1 0-6 0h-3v4h12v-4h-3Z"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path d="M25.5 5.5h3v26h-24v-26h3" strokeMiterlimit="10" />
  </StrokeSVG>
);

IconSVG["Paper"] = () => (
  <StrokeSVG viewBox="0 0 32 32">
    <path
      d="M26.5 22.5v-21h-23v27a3 3 0 0 0 3 3h2"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
    <path
      d="M9.5 28.5a3 3 0 0 1-3 3h21a3 3 0 0 0 3-3v-2h-21v2Zm0-21h11m-11 6h11m-11 6h11"
      strokeMiterlimit="10"
      strokeLinecap="square"
    />
  </StrokeSVG>
);

IconSVG["Lightbulb"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path
      fillRule="evenodd"
      d="M9.01 14h-2c-.55 0-1 .45-1 1s.45 1 1 1h2c.55 0 1-.45 1-1s-.44-1-1-1Zm1-3h-4c-.55 0-1 .45-1 1s.45 1 1 1h4c.55 0 1-.45 1-1s-.44-1-1-1Zm-2-11C5.26 0 3.03 1.95 3.03 4.35c0 2.37 1.63 2.64 1.94 5.22 0 .24.22.44.5.44h5.09c.28 0 .5-.19.5-.44C11.37 6.99 13 6.72 13 4.35 13 1.95 10.77 0 8.01 0Z"
      clipRule="evenodd"
    />
  </FillSVG>
);

IconSVG["ZipFile"] = () => (
  <FillSVG viewBox="0 0 30 30">
    <path
      fill="#2b5469"
      fill-opacity=".7"
      d="M29.756 10.508A1.25 1.25 0 0 0 28.75 10H6.25a1.25 1.25 0 0 0-1.195.883L2.5 19.186V3.75h6.831l2.125 3.194A1.25 1.25 0 0 0 12.5 7.5h10v1.25H25v-2.5A1.25 1.25 0 0 0 23.75 5H13.169l-2.125-3.194A1.25 1.25 0 0 0 10 1.25H1.25A1.25 1.25 0 0 0 0 2.5v25h.031c-.01.264.064.524.213.742a1.25 1.25 0 0 0 1.006.508h22.5a1.25 1.25 0 0 0 1.195-.883l5-16.25a1.25 1.25 0 0 0-.189-1.11"
    />
    <path
      fill="#fff"
      d="M10.381 21v-.306l2.376-3.549h-2.274v-.483h3.051v.294l-2.397 3.555h2.385V21zm4.612 0v-4.338h.555V21zm2.3 0v-4.338h1.467q.465 0 .827.156.364.156.57.462.21.306.21.753 0 .432-.195.738a1.2 1.2 0 0 1-.537.465q-.345.159-.8.159h-.973V21zm.567-2.073h.963q.446 0 .726-.249t.279-.678q0-.432-.288-.654t-.744-.222h-.936z"
    />
  </FillSVG>
);

IconSVG["DownloadArrow"] = () => (
  <FillSVG viewBox="0 0 12 12">
    <path d="M11 10H1a1 1 0 0 0 0 2h10a1 1 0 0 0 0-2M6.5 0h-1a.5.5 0 0 0-.5.5V3H2.5a.5.5 0 0 0-.376.829l3.5 4a.5.5 0 0 0 .752 0l3.5-4A.5.5 0 0 0 9.5 3H7V.5a.5.5 0 0 0-.5-.5" />
    <clipPath id="a">
      <path fill="#fff" d="M0 0h12v12H0z" />
    </clipPath>
  </FillSVG>
);

IconSVG["DownloadArrowThin"] = () => (
  <StrokeSVG viewBox="0 0 12 12">
    <path d="M10 5.5L6 9.5L2 5.5" />
    <path d="M6 9.5V0.5" />
    <path d="M11.5 11.5H0.5" />
  </StrokeSVG>
);

IconSVG["CompleteCircle"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM7.35355 11.0464C7.15829 11.2417 6.84171 11.2417 6.64645 11.0464L3.95355 8.35355C3.75829 8.15829 3.75829 7.84171 3.95355 7.64645L4.64645 6.95355C4.84171 6.75829 5.15829 6.75829 5.35355 6.95355L7 8.6L10.6464 4.95355C10.8417 4.75829 11.1583 4.75829 11.3536 4.95355L12.0464 5.64645C12.2417 5.84171 12.2417 6.15829 12.0464 6.35355L7.35355 11.0464Z" />
  </FillSVG>
);

IconSVG["WarningCircle"] = () => (
  <FillSVG viewBox="0 0 16 16">
    <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 12C7.4 12 7 11.6 7 11C7 10.4 7.4 10 8 10C8.6 10 9 10.4 9 11C9 11.6 8.6 12 8 12ZM9 9H7V4H9V9Z" />
  </FillSVG>
);

IconSVG["Filter"] = () => (
  <FillSVG viewBox="0 0 12 12">
    <path d="M11.941.264A.5.5 0 0 0 11.5 0H.5a.5.5 0 0 0-.416.777L4 6.651V11.5a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 .5-.5V6.651L11.916.777a.5.5 0 0 0 .025-.513" />
  </FillSVG>
);

IconSVG["FilterSliders"] = () => (
  <StrokeSVG viewBox="0 0 12 11">
    <path d="M3.5 5.5V0.5" />
    <path d="M0.5 3H3.5" />
    <path d="M5.5 3H11.5" />
    <path d="M8.5 10.5V5.5" />
    <path d="M11.5 8H8.5" />
    <path d="M6.5 8H0.5" />
  </StrokeSVG>
);

IconSVG["Next"] = () => (
  <StrokeSVG viewBox="0 0 16 16">
    <path d="M6.5 3.5 11 8l-4.5 4.5" />
  </StrokeSVG>
);

IconSVG["Tablet"] = () => (
  <StrokeSVG viewBox="0 0 24 24" strokeWidth={1.6}>
    <path d="M6.2 2.5h11.6a2.2 2.2 0 0 1 2.2 2.2v14.6a2.2 2.2 0 0 1-2.2 2.2H6.2a2.2 2.2 0 0 1-2.2-2.2V4.7a2.2 2.2 0 0 1 2.2-2.2Z" />
    <path d="M10.4 18.5h3.2" />
  </StrokeSVG>
);

const iconKinds = Object.keys(IconSVG).sort((a, b) =>
  ascending(a.toLowerCase(), b.toLowerCase()),
);

export { iconKinds, IconSVG };
